/**
 * Jersey Personalization & Customizer Logic
 * Conforms strictly to CRO development guidelines in AGENTS.md:
 * - Pure business logic functions separated from DOM
 * - Data attribute DOM access only
 * - Event delegation on document for dynamic/AJAX/Quick View compatibility
 * - Defensive try/catch wrapping
 * - Zero global variables
 */

(function () {
  'use strict';

  /**
   * Pure business logic: Parse player string into uppercase name and clean numeric digits.
   * Handles formats like "Hinata #10", "Kageyama 9", "Oikawa", "Bokuto #4", etc.
   * @param {string} playerString
   * @returns {{ name: string, number: string }}
   */
  function getParsedPlayerDetails(playerString) {
    if (!playerString || typeof playerString !== 'string') {
      return { name: '', number: '' };
    }

    const cleanString = playerString.trim();

    if (cleanString.includes('#')) {
      const parts = cleanString.split('#');
      const parsedName = (parts[0] || '').trim().toUpperCase();
      const parsedNumber = (parts[1] || '').replace(/[^0-9]/g, '').trim();
      return { name: parsedName, number: parsedNumber };
    }

    const numberMatch = cleanString.match(/^(.*?)\s*#?\s*(\d+)$/);
    if (numberMatch) {
      const matchedName = (numberMatch[1] || '').trim().toUpperCase();
      const matchedNumber = (numberMatch[2] || '').trim();
      return { name: matchedName, number: matchedNumber };
    }

    return { name: cleanString.toUpperCase(), number: '' };
  }

  /**
   * Updates state classes and aria attributes on player buttons within a container
   * @param {NodeList|Element[]} buttons
   * @param {HTMLElement} activeButton
   */
  function updatePlayerButtonsState(buttons, activeButton) {
    if (!buttons || !activeButton) return;

    buttons.forEach(function (button) {
      button.classList.remove('is-selected');
      button.setAttribute('aria-pressed', 'false');
    });

    activeButton.classList.add('is-selected');
    activeButton.setAttribute('aria-pressed', 'true');
  }

  /**
   * Handles player button click via event delegation
   * @param {HTMLElement} button
   */
  function handlePlayerButtonClick(button) {
    try {
      if (!button) return;

      const customizerElement = button.closest('[data-jersey-customizer]');
      if (!customizerElement) return;

      const buttons = customizerElement.querySelectorAll('[data-player-button]');
      const customBox = customizerElement.querySelector('[data-jersey-custom-box]');
      const nameInput = customizerElement.querySelector('[data-jersey-input="name"]');
      const numberInput = customizerElement.querySelector('[data-jersey-input="number"]');

      const playerValue = button.getAttribute('data-player-value') || '';
      updatePlayerButtonsState(buttons, button);

      const isCustom = playerValue.toLowerCase() === 'custom';

      const presetMode = customizerElement.getAttribute('data-preset-mode') || 'both';

      if (isCustom) {
        if (customBox) {
          customBox.classList.remove('is-hidden');
        }
        if (nameInput) {
          nameInput.value = '';
          nameInput.disabled = false;
          try {
            nameInput.focus({ preventScroll: true });
          } catch (e) {
            // fallback
          }
        }
        if (numberInput) {
          numberInput.value = '';
          numberInput.disabled = false;
        }
      } else {
        const parsedDetails = getParsedPlayerDetails(playerValue);

        if (nameInput) {
          // If preset mode is number-only, do not populate jersey name for preset player buttons
          if (presetMode === 'number-only') {
            nameInput.value = '';
          } else {
            nameInput.value = parsedDetails.name;
          }
          nameInput.disabled = false;
        }
        if (numberInput) {
          numberInput.value = parsedDetails.number;
          numberInput.disabled = false;
        }
        if (customBox) {
          customBox.classList.add('is-hidden');
        }
      }
    } catch (error) {
      console.warn('[JerseyCustomizer] Player button click error:', error);
    }
  }

  /**
   * Initializes delegated click listeners for jersey personalization
   */
  function initJerseyEvents() {
    try {
      document.addEventListener('click', function (event) {
        try {
          const playerButton = event.target.closest('[data-player-button]');
          if (playerButton) {
            event.preventDefault();
            handlePlayerButtonClick(playerButton);
          }
        } catch (err) {
          console.warn('[JerseyCustomizer] Delegated click error:', err);
        }
      });

      document.addEventListener('input', function (event) {
        try {
          const targetElement = event.target;
          if (!targetElement || !targetElement.dataset) return;

          const inputType = targetElement.dataset.jerseyInput;

          if (inputType === 'name') {
            targetElement.value = targetElement.value.toUpperCase();
          }

          if (inputType === 'number') {
            targetElement.value = targetElement.value.replace(/[^0-9]/g, '').slice(0, 3);
          }
        } catch (error) {
          console.warn('[JerseyCustomizer] Input sanitization error:', error);
        }
      });
    } catch (error) {
      console.warn('[JerseyCustomizer] Init events error:', error);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initJerseyEvents);
  } else {
    initJerseyEvents();
  }
})();
